import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import FAQ from "@/pages/FAQ";
import Operations from "@/pages/Operations";
import CookieBanner from "@/components/CookieBanner";
import "@/App.css";

function ProtectedRoute({ children, requireSubscription = false, requireAdmin = false }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-electric-blue font-mono label-mono animate-pulse">LOADING NEXUS...</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (requireSubscription && !user.subscription_active) {
        return <Navigate to="/checkout/cancel" replace />;
    }
    // Admin-gated routes look like 404 to non-admins (no redirect → no URL discovery).
    if (requireAdmin && !user.is_admin) {
        return <NotFound />;
    }
    return children;
}

function NotFound() {
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
            <p className="font-heading font-black text-7xl md:text-9xl text-electric-blue tracking-tighter">404</p>
            <p className="label-mono text-muted-foreground mt-4 mb-8">PAGE NOT FOUND</p>
            <a
                href="/"
                className="bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-8 py-3.5 text-xs tracking-wider uppercase transition-all hover:shadow-[0_0_24px_rgba(0,123,255,0.5)]"
            >
                Back to Home
            </a>
        </div>
    );
}

function App() {
    return (
        <div className="App font-body">
            <AuthProvider>
                <BrowserRouter>
                    <Toaster
                        theme="dark"
                        position="top-right"
                        toastOptions={{
                            style: {
                                background: "#0A0A0A",
                                color: "#FFFFFF",
                                border: "1px solid rgba(0,123,255,0.3)",
                            },
                        }}
                    />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/operations" element={<Operations />} />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute requireSubscription>
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <Admin />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/checkout/success"
                            element={
                                <ProtectedRoute>
                                    <CheckoutSuccess />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/checkout/cancel"
                            element={
                                <ProtectedRoute>
                                    <CheckoutCancel />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <CookieBanner />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
